import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type {
  EditInventoryLocation,
  InventoryLocation,
} from '../../../shared/models/api/inventory-location.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { InventoryLocationService } from '../../../shared/services/api/inventory-location.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import type { EditInventoryLocationFormValue } from './inventory-location-edit.models';

@Component({
  selector: 'opa-inventory-location-edit',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './inventory-location-edit.component.html',
  styleUrl: './inventory-location-edit.component.scss',
})
export class InventoryLocationEditComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly inventoryLocationService = inject(InventoryLocationService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  @Input({ required: true }) open = false;
  @Input({ required: true }) inventoryLocation: InventoryLocation | undefined = undefined;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<InventoryLocationEditComponent>): void {
    if (changes.open && this.open) this.resetForm(this.inventoryLocation);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid || !this.inventoryLocation) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const location = this.formValueToLocation(formValue);
    this.subSink.sink = this.inventoryLocationService
      .editInventoryLocation(this.inventoryLocation.id, location)
      .pipe(
        tap({
          next: (editedLocation) => {
            this.toastManager.addMessage({
              text: `Location '${editedLocation.description}' edited`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<EditInventoryLocationFormValue>> {
    const form = this.formBuilder.group<ControlsOf<EditInventoryLocationFormValue>>({
      description: this.formBuilder.control(null, [OpaValidators.required()]),
      requiresAssignee: this.formBuilder.control(null),
    });

    return form;
  }

  private formValueToLocation(form: EditInventoryLocationFormValue): EditInventoryLocation {
    const location: EditInventoryLocation = {
      description: form.description!,
      requiresAssignee: !!form.requiresAssignee,
      name: this.inventoryLocation!.name,
    };
    return location;
  }

  private resetForm(inventoryLocation?: InventoryLocation): void {
    this.form.reset();
    if (!inventoryLocation) return;

    this.form.patchValue({
      description: inventoryLocation.description,
      requiresAssignee: inventoryLocation.requiresAssignee,
    });
  }
}
